import { MAX_SELECTED_VALUES_IN_CONDITIONS } from "@entrilia/shared/components/reporting/conditions.ts";
import { defined } from "@entrilia/shared/utilities/typeHelper.ts";
import {
  financialStatementGeneralSettingsSchema,
  listGeneralSettingsSchema,
  pivotGeneralSettingsSchema,
  tabularGeneralSettingsSchema,
  tileSettingsSchema,
} from "../../../../../api/schemas/biClientSchema.ts";
import type { ConditionConfiguration, ConditionDescriptor } from "../../../../../api/types/fieldConfigurationTypes.ts";
import { CalculationType, MeasureDateRange } from "../../../../../api/types/fieldConfigurationTypes.ts";
import type {
  ConditionField,
  DimensionDescriptor,
  DimensionField,
  FinancialStatementConfiguration,
  FinancialStatementGeneralSettings,
  ListGeneralSettings,
  PivotGeneralSettings,
  ReportField,
  StatementLineConfiguration,
  TabularGeneralSettings,
  TileSettings,
} from "../../../../../api/types/svTypes.ts";
import { AreaItemType, DimensionFieldType, ItemDataType, ReportType } from "../../../../../api/types/svTypes.ts";
import { isReferenceDateField } from "../components/builder/common/utilities/isReferenceDateField.ts";
import {
  isValueLine,
  readsPeriodFromField,
  resolveLinePeriods,
} from "../components/builder/financialStatement/utils/statementLines.ts";
import { getColumnPeriodForLine } from "../components/builder/financialStatement/utils/statementColumnPeriod.ts";
import { isAllowedInConditions } from "./conditionDataTypes.ts";
import { createLeafConditionDescriptor, getConditionLeafFilters } from "./conditionDescriptorHelper.ts";
import {
  isIrr,
  hasValidConditionReference,
  isAggregationBased,
  isCustomColumn,
  isDimension,
  isDimensionBased,
  isMeasure,
} from "./fieldsHelper.ts";
import { getValidConditionDescriptors } from "./getValidConditions.ts";
import { toAmountType, toCalculateBy } from "./measureConverter.ts";

export enum InvalidConfigurationReason {
  ConditionsFieldAndCustomConditions = 1,
  FieldAndCustomConditions = 2,
  MultipleFields = 3,
  MultipleCustomConditions = 4,
  NoDateFields = 5,
  MandatoryFieldIsNotSelected = 6,
  TooManySelectedValuesInConditions = 7,
}

export function isTabularConfigurationValid(
  conditions: ConditionField[],
  fields: ReportField[],
  dimensions: DimensionDescriptor[],
  reportType: ReportType = ReportType.Tabular
): MeasureValidReturnType & { measure?: string } {
  if (fields.length === 0) return { valid: false, validation: "error" };
  if (
    reportType === ReportType.CashFlow &&
    (fields.filter(isIrr).length !== 1 ||
      fields.some((field) => !isIrr(field) && (!isDimension(field) || field.meta.type !== ItemDataType.General)))
  ) {
    return { valid: false, validation: "error" };
  }
  const validConditions = getValidConditionDescriptors(conditions);
  const dimensionFields = getDimensionFields(fields);
  const measureFields = getMeasureFields(fields, conditions);

  const invalidMeasure = measureFields
    .map((mf) => {
      const valid = isMeasureValid(validConditions, dimensionFields, mf, dimensions);
      return { ...valid, measure: mf.guid };
    })
    .find((result) => result.valid === false);

  if (invalidMeasure !== undefined) {
    return invalidMeasure;
  }
  if (
    !areMandatoryFieldsSet(
      conditions,
      fields
        .flatMap((f) => f.config.customConditions || [])
        .filter((c) => isCustomConditionAllowed(c, dimensions, reportType))
    )
  ) {
    return { valid: false, validation: "error", reason: InvalidConfigurationReason.MandatoryFieldIsNotSelected };
  }

  if (areTooManySelectedValuesInConditions(conditions)) {
    return {
      valid: false,
      validation: "error",
      reason: InvalidConfigurationReason.TooManySelectedValuesInConditions,
    };
  }

  return { valid: true, validation: "valid" };
}

const areTooManySelectedValuesInConditions = (conditions: ConditionField[]) => {
  return conditions.some(
    (c) => !c.validation?.invalid && c.config.filter?.values.length > MAX_SELECTED_VALUES_IN_CONDITIONS
  );
};

export const isTileConfigurationValid = (
  conditions: ConditionField[],
  fields: ReportField[],
  dimensions: DimensionDescriptor[]
): MeasureValidReturnType & { measure?: string } => {
  return isTabularConfigurationValid(conditions, fields, dimensions, ReportType.Tile);
};

export const isListConfigurationValid = (
  conditions: ConditionField[],
  fields: ReportField[]
): ConfigurationValidReturnType => {
  if (!fields.some(isDimensionBased)) {
    return { valid: false, validation: "error" };
  }
  if (!areMandatoryFieldsSet(conditions, [])) {
    return { valid: false, validation: "error", reason: InvalidConfigurationReason.MandatoryFieldIsNotSelected };
  }
  if (areTooManySelectedValuesInConditions(conditions)) {
    return {
      valid: false,
      validation: "error",
      reason: InvalidConfigurationReason.TooManySelectedValuesInConditions,
    };
  }
  return { valid: true, validation: "valid" };
};

type StatementValidityInput = Pick<FinancialStatementConfiguration, "lines" | "columns" | "settings">;

const withFieldPeriod = (line: StatementLineConfiguration): StatementLineConfiguration =>
  readsPeriodFromField(line.lineType) ? { ...line, dateRange: line.field?.dateRange ?? undefined } : line;

const toSetConditionDescriptors = (conditions: ConditionConfiguration[]): ConditionDescriptor[] =>
  conditions
    .filter((condition) => condition.filter.values.length > 0)
    .map((condition) => createLeafConditionDescriptor(condition.filter));

const findInvalidStatementLine = (
  conditions: ConditionField[],
  { lines, columns, settings }: StatementValidityInput,
  dimensions: DimensionDescriptor[]
): ConfigurationValidReturnType | undefined => {
  const referenceDates = dimensions.filter(isReferenceDateField);
  const axisFields = columns.axes.flatMap(({ field }): DimensionField[] => {
    const meta = referenceDates.find((dimension) => dimension.name === field.name);
    return meta
      ? [{ type: DimensionFieldType.DIMENSION, areaItemType: AreaItemType.COLUMNS, meta, config: field }]
      : [];
  });
  const reportConditions = getValidConditionDescriptors(conditions);
  const periods = resolveLinePeriods(lines.map(withFieldPeriod), settings.defaultDateRange);
  const dataColumns = columns.dataColumns.filter((column) => column.columnType === "Data");

  const checks = lines.flatMap((line) => {
    const { field } = line;
    const period = periods[line.guid];
    if (!field || period === undefined) {
      return [];
    }
    const lineField = getMeasure(conditions, { config: field });
    return dataColumns.map((column) => ({
      measure: field.guid,
      result: isMeasureValid(
        [...reportConditions, ...toSetConditionDescriptors(column.customConditions)],
        axisFields,
        { ...lineField, dateRangeType: getColumnPeriodForLine(column, lines, line.guid) ?? period },
        referenceDates
      ),
    }));
  });
  const invalid = checks.find((check) => !check.result.valid);
  return invalid && { ...invalid.result, measure: invalid.measure };
};

export const isFinancialStatementConfigurationValid = (
  conditions: ConditionField[],
  statement: StatementValidityInput,
  dimensions: DimensionDescriptor[]
): ConfigurationValidReturnType => {
  const hasValueLine = statement.lines.some(isValueLine);
  const hasDataColumn = statement.columns.dataColumns.some((column) => column.columnType === "Data");
  if (!hasValueLine || !hasDataColumn) {
    return { valid: false, validation: "error" };
  }
  const lineConditions = statement.lines.flatMap((line) => line.field?.customConditions ?? []);
  if (!areMandatoryFieldsSet(conditions, lineConditions)) {
    return { valid: false, validation: "error", reason: InvalidConfigurationReason.MandatoryFieldIsNotSelected };
  }
  if (areTooManySelectedValuesInConditions(conditions)) {
    return {
      valid: false,
      validation: "error",
      reason: InvalidConfigurationReason.TooManySelectedValuesInConditions,
    };
  }
  return findInvalidStatementLine(conditions, statement, dimensions) ?? { valid: true, validation: "valid" };
};

export function isPivotConfigurationValid(
  conditions: ConditionField[],
  fields: ReportField[],
  measures: ReportField[],
  dimensions: DimensionDescriptor[],
  reportType: ReportType = ReportType.Pivot
): MeasureValidReturnType & { measure?: string } {
  if (fields.length === 0) return { valid: false, validation: "error" };
  const calculatedFields = measures.concat(fields.filter(isAggregationBased));

  const invalidMeasure = calculatedFields
    .map((field) => ({
      ...isMeasureConfigurationValid(conditions, fields, dimensions, field),
      measure: field.config.guid,
    }))
    .find((result) => result.valid === false);

  if (invalidMeasure !== undefined) {
    return invalidMeasure;
  }

  if (
    !areMandatoryFieldsSet(
      conditions,
      calculatedFields
        .flatMap((f) => f.config.customConditions || [])
        .filter((c) => isCustomConditionAllowed(c, dimensions, reportType))
    )
  ) {
    return { valid: false, validation: "error", reason: InvalidConfigurationReason.MandatoryFieldIsNotSelected };
  }

  if (areTooManySelectedValuesInConditions(conditions)) {
    return {
      valid: false,
      validation: "error",
      reason: InvalidConfigurationReason.TooManySelectedValuesInConditions,
    };
  }

  return { valid: true, validation: "valid" };
}

export function isMeasureConfigurationValid(
  conditions: ConditionField[],
  fields: ReportField[],
  dimensions: DimensionDescriptor[],
  value: ReportField
): MeasureValidReturnType {
  if (isCustomColumn(value) && value.config.calculationType === CalculationType.ConditionValue) {
    const valid = hasValidConditionReference(value, conditions);
    return { valid, validation: valid ? "valid" : "error" };
  }
  const validConditions = getValidConditionDescriptors(conditions);
  const measureField = getMeasureField(conditions, value);

  const valid = isMeasureValid(validConditions, fields, measureField, dimensions);
  return valid;
}

export function isMeasureValid(
  conditions: ConditionDescriptor[],
  fields: ReportField[],
  calculatedField: CalculatedField,
  dimensions: DimensionDescriptor[]
): MeasureValidReturnType {
  const conditionDates = getDateConditions(conditions, dimensions);
  const dateFields = getDateFields(fields);
  const customConditionDate = getDateCustomConditions(calculatedField, dimensions);

  if (dateFields.length > 0) {
    const referenceDateField = getDimensionDescriptor(defined(dateFields[0]).meta.name, dimensions);
    if (customConditionDate.some((ccd) => ccd.dimensionName === referenceDateField.name)) {
      return {
        valid: false,
        validation: "error",
        referenceDateField,
        reason: InvalidConfigurationReason.FieldAndCustomConditions,
      };
    }
    return { valid: true, validation: "valid", referenceDateField };
  }

  if (conditionDates.length > 0) {
    const referenceDateField = getDimensionDescriptor(defined(conditionDates[0]).dimensionName, dimensions);
    return { valid: true, validation: "valid", referenceDateField };
  }

  if (customConditionDate.length > 0) {
    const referenceDateField = getDimensionDescriptor(defined(customConditionDate[0]).dimensionName, dimensions);
    if (customConditionDate.length === 1) return { valid: true, validation: "valid", referenceDateField };
    return {
      valid: false,
      validation: "error",
      referenceDateField,
      reason: InvalidConfigurationReason.MultipleCustomConditions,
    };
  }

  if (calculatedField.dateRangeType === MeasureDateRange.NetChange) return { valid: true, validation: "valid" };

  return { valid: false, validation: "error", reason: InvalidConfigurationReason.NoDateFields };
}

function getDateConditions(conditions: ConditionDescriptor[], dimensions: DimensionDescriptor[]) {
  return getConditionLeafFilters(conditions).filter((c) => {
    const meta = dimensions.find((d) => d.name === c.dimensionName);
    if (!meta) {
      return false;
    }
    return meta.options?.asDateReference === true;
  });
}

function getDateFields(fields: ReportField[]) {
  return fields.filter((f) => "options" in f.meta && f.meta.options?.asDateReference === true);
}

function getDateCustomConditions(field: CalculatedField, dimensions: DimensionDescriptor[]) {
  if (!field.conditions) return [];
  return getConditionLeafFilters(field.conditions).filter((c) => {
    const meta = dimensions.find((d) => d.name === c.dimensionName);
    if (!meta) {
      return false;
    }
    return meta.options?.asDateReference === true;
  });
}

export function getDimensionFields(fields: ReportField[]) {
  return fields.filter(isDimension);
}

function getMeasureFields(fields: ReportField[], conditions: ConditionField[]) {
  return fields.filter(isAggregationBased).map((measure) => getMeasureField(conditions, measure));
}

function getDimensionDescriptor(name: string, dimensions: DimensionDescriptor[]) {
  return defined(dimensions.find((d) => d.name === name));
}

function getMeasureField(conditions: ConditionField[], field: ReportField): CalculatedField {
  const measureOptions = getMeasure(conditions, field);
  return measureOptions;
}

function getMeasure(conditions: ConditionField[], measure: Pick<ReportField, "config">): CalculatedField {
  const customConditions = getCustomConditions(measure);
  const linkedConditions = getLinkedConditions(measure, conditions);
  const result = mergeConditions(customConditions, linkedConditions);

  return {
    guid: measure.config.guid,
    dateRangeType: measure.config.dateRange || MeasureDateRange.EndingBalance,
    conditions: result,
  };
}

export function getMeasureCalculateByField(measure: ReportField) {
  if (isMeasure(measure)) {
    return measure.config.calculateByField || toCalculateBy(measure.meta.units);
  }
  return measure.config.calculateByField;
}

export function getMeasureAmountTypeField(measure: ReportField) {
  if (isMeasure(measure)) {
    return measure.config.amountType || toAmountType(measure.meta.units);
  }
  return measure.config.amountType;
}

function getCustomConditions(value: Pick<ReportField, "config">) {
  if (!value.config.customConditions) return [];
  return value.config.customConditions
    .filter((cc) => cc.filter.values.length > 0)
    .map((cc): ConditionDescriptor => createLeafConditionDescriptor(cc.filter));
}

function getLinkedConditions(value: Pick<ReportField, "config">, conditions: ConditionField[]) {
  if (!value.config.linkedConditions) return [];
  return value.config.linkedConditions
    .map((lc) => conditions.find((c) => c.config.guid === lc)?.config.filter)
    .filter((filter): filter is ConditionConfiguration["filter"] => (filter?.values.length || 0) > 0)
    .map((c) => createLeafConditionDescriptor(structuredClone(c)));
}

function mergeConditions(custom: ConditionDescriptor[], linked: ConditionDescriptor[]) {
  const allConditions = [...custom, ...linked];
  return allConditions;
}

function filterUnsetMandatoryField(config: ConditionConfiguration) {
  return config.mandatory && config.filter.values.length === 0;
}

const isCustomConditionAllowed = (
  condition: ConditionConfiguration,
  dimensions: DimensionDescriptor[],
  reportType: ReportType
): boolean => {
  const dimension = dimensions.find((d) => d.name === condition.filter.dimensionName);
  return dimension === undefined || isAllowedInConditions(dimension, reportType);
};

function areMandatoryFieldsSet(conditions: ConditionField[], customConditions: ConditionConfiguration[]) {
  return (
    conditions.filter((c) => !c.validation?.invalid && filterUnsetMandatoryField(c.config)).length === 0 &&
    customConditions.filter(filterUnsetMandatoryField).length === 0
  );
}

export type MeasureValidReturnType = {
  valid: boolean;
  validation: Validation;
  referenceDateField?: DimensionDescriptor;
  reason?: InvalidConfigurationReason;
};

export type Validation = "valid" | "warning" | "error";

export type ConfigurationValidReturnType = ReturnType<typeof isTabularConfigurationValid>;

export function createTileDefaultSettings(): TileSettings {
  return tileSettingsSchema.parse({});
}

export function createListDefaultSettings(): ListGeneralSettings {
  return listGeneralSettingsSchema.parse({});
}

export function createFinancialStatementDefaultSettings(): FinancialStatementGeneralSettings {
  return financialStatementGeneralSettingsSchema.parse({});
}

export function createTabularDefaultSettings(): TabularGeneralSettings {
  return tabularGeneralSettingsSchema.parse({});
}

export function createPivotDefaultSettings(): PivotGeneralSettings {
  return pivotGeneralSettingsSchema.parse({});
}

export type CalculatedField = {
  guid: string;
  dateRangeType: MeasureDateRange;
  conditions: ConditionDescriptor[];
};
